import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Award, Building2, Gift, Handshake, ShieldCheck, Truck, Home, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { BRAND, whatsappLink } from "../mock/mock";
import { WhatsAppIcon } from "../components/WhatsAppFloat";

const WholesalePage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", city: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.phone) {
      toast({ title: "Fill required fields", description: "Company, contact name and phone are required." });
      return;
    }
    const text = [
      `Hello Agrawal Rich Dry Fruits,`,
      ``,
      `Wholesale / Bulk enquiry:`,
      ``,
      `Company: ${form.company}`,
      `Contact: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email && `Email: ${form.email}`,
      form.city && `City: ${form.city}`,
      ``,
      `Requirement:`,
      form.message || `(please share your requirement)`,
    ].filter(Boolean).join("\n");
    window.open(whatsappLink(text), "_blank", "noopener,noreferrer");
    toast({ title: "Opening WhatsApp", description: "Your wholesale enquiry has been prepared." });
    setForm({ company: "", name: "", email: "", phone: "", city: "", message: "" });
  };

  const waHref = whatsappLink("Hello Agrawal Rich Dry Fruits, I'd like a wholesale / bulk quote.");

  return (
    <main className="bg-cream">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2a1a15]/90 via-[#2a1a15]/70 to-[#2a1a15]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-16 md:py-24 text-white">
          <div className="flex items-center gap-2 text-[12.5px] text-[#f2e7d1]">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-gold"><Home className="h-3.5 w-3.5" /> Home</Link>
            <span>/</span><span className="text-gold">Bulk / Wholesale</span>
          </div>
          <div className="text-gold text-[11px] tracking-[0.28em] font-bold mt-4">FOR BUSINESSES & GIFTING</div>
          <h1 className="mt-2 font-serif-brand text-[38px] md:text-[56px] font-bold leading-tight max-w-3xl">Wholesale, Corporate & Custom Gifting</h1>
          <p className="mt-4 text-[16px] text-[#f2e7d1] max-w-xl">Premium dry fruits at wholesale prices, curated corporate hampers, and custom-branded packaging — all backed by 50+ years of trust.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold px-6 py-3 rounded-full transition-colors"><WhatsAppIcon className="h-5 w-5" /> WhatsApp Quote</a>
            <a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white hover:text-[#2a1a15] font-medium px-6 py-3 rounded-full transition-colors">Call {BRAND.phone}</a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-4">
        {[
          { i: Building2, t: "Bulk Orders", d: "Volume pricing for retailers, hotels, restaurants and distributors across India." },
          { i: Gift, t: "Corporate Gifting", d: "Elegant hampers customized with your brand for festivals, launches and clients." },
          { i: Handshake, t: "Distribution", d: "Partner with a 50-year-old brand — reliable supply, marketing support & margins." },
        ].map((x) => (
          <div key={x.t} className="bg-white border border-[#efe4c9] rounded-2xl p-6">
            <x.i className="h-8 w-8 text-brand" />
            <h3 className="font-serif-brand text-xl font-bold text-[#2a1a15] mt-3">{x.t}</h3>
            <p className="text-[13.5px] text-[#6b4f39] mt-1.5 leading-relaxed">{x.d}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 pb-16 grid md:grid-cols-2 gap-8 items-start">
        <div>
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive">WHY PARTNER WITH US</div>
          <h2 className="font-serif-brand text-[28px] md:text-[38px] font-bold text-[#2a1a15] mt-1">A brand your customers will love</h2>
          <ul className="mt-5 space-y-3.5">
            {[
              { i: Award, t: "50+ years of trust", d: "A legacy brand across generations, with strong recall." },
              { i: ShieldCheck, t: "Quality-checked every batch", d: "Multi-level QC and food-safety compliant packing." },
              { i: Truck, t: "Pan India supply", d: "Reliable, timely delivery via our nationwide network." },
              { i: Gift, t: "Custom packaging", d: "Custom branding & elegant designs for festive gifting." },
            ].map((f, i) => (
              <li key={i} className="flex gap-3">
                <div className="h-10 w-10 rounded-full bg-[#f9f1de] flex items-center justify-center shrink-0"><f.i className="h-5 w-5 text-brand" /></div>
                <div>
                  <div className="font-semibold text-[15px] text-[#2a1a15]">{f.t}</div>
                  <div className="text-[13.5px] text-[#6b4f39]">{f.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#efe4c9] rounded-2xl p-6 md:p-8">
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive">WHOLESALE ENQUIRY</div>
          <h3 className="font-serif-brand text-2xl font-bold text-[#2a1a15] mt-1">Tell us what you need</h3>
          <p className="mt-1 text-[12.5px] text-[#6b4f39]">Your enquiry is sent to us on WhatsApp for a personal, fast response.</p>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div><label className="text-[12.5px] font-medium">Company / Brand *</label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" /></div>
            <div><label className="text-[12.5px] font-medium">Contact Name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" /></div>
            <div><label className="text-[12.5px] font-medium">Phone *</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" /></div>
            <div><label className="text-[12.5px] font-medium">Email</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" className="mt-1 h-11 bg-cream border-[#e3d3ad]" /></div>
            <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">City</label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-11 bg-cream border-[#e3d3ad]" /></div>
            <div className="sm:col-span-2"><label className="text-[12.5px] font-medium">Requirement details</label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-cream border-[#e3d3ad]" placeholder="Products, quantities, timeline, branding needs..." /></div>
          </div>

          <Button type="submit" className="mt-5 w-full h-12 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold">
            <WhatsAppIcon className="h-5 w-5 mr-1.5" /> Send via WhatsApp
          </Button>
        </form>
      </section>
    </main>
  );
};

export default WholesalePage;
